import { BrButton } from '@govbr-ds/react-components';
import React, { useContext, useState } from 'react';
import styles from './titularArrendatarioOutrosRegimes.module.scss';
import CustomStep from '../../customStep/customStep';
import { mesParaAbreviacao } from '../../../../../utils/mesParaAbreviacao';
import DadosOperacaoOutrosRegimes from './components/dadosOperacao/dadosOperacao';
import ComponentesBaseCalculoOutrosRegimes from './components/componentesBaseCalculo/componentesBaseCalculo';
import { useBackEndRequests } from '../../../lib/backEndRequests';
import { IDadosOutrosRegimesEnviar, PaginasOutrosRegimes } from '../../../../../interfaces/IDadosOutrosRegimes';
import { useNotificacao } from '../../../../../hooks/useNotificacao';
import { DiefCfemContext } from '../../../../../contexts/DiefCfemContext';
import { validarDocumento } from '../../../../../utils/validarDocumento';

interface ITitularArrendatarioOutrosRegimesProps {
  setIsEdicaoOutrosRegimes: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IDadosOperacaoBody {
  idDeclaracao: number;
  estabelecimentoMinerador: string;
  tipoVinculoProcesso: number;
  situacaoOperacionalRelativaFaturamento: number;
  situacaoOperacionalRelativaProducao: number;
  observacoes: string;
}

const TitularArrendatarioOutrosRegimes = ({
  setIsEdicaoOutrosRegimes,
}: Readonly<ITitularArrendatarioOutrosRegimesProps>) => {
  const [stepFormulario, setStepFormulario] = useState(1);
  const [baseDeCalculo, setBaseDeCalculo] = useState<PaginasOutrosRegimes>('lista');
  const [hasProducao, setHasProducao] = useState(true);
  const [hasFatoGerador, setHasFatoGerador] = useState(true);
  const [estabelecimentoMinerador, setEstabelecimentoMinerador] = useState('');
  const [titularOuArrendatario, setTitularOuArrendatario] = useState(0);
  const [observacoes, setObservacoes] = useState('');

  const mostrarNotificacao = useNotificacao();

  const buttonsHtml = (
    onClickFirstButton: () => void,
    onClickSecondButton: () => void,
    textFirstButton: React.ReactElement,
    textSecondButton: React.ReactElement
  ) => {
    return (
      <>
        <BrButton secondary onClick={onClickFirstButton}>
          <span className={styles.button}>{textFirstButton}</span>
        </BrButton>
        <BrButton onClick={onClickSecondButton} primary>
          <span className={styles.button}>{textSecondButton}</span>
        </BrButton>
      </>
    );
  };

  const context = useContext(DiefCfemContext);

  const idOperacao = context?.idOperacaoEditar || '';

  const { putSalvarDadosOutrosRegimes } = useBackEndRequests();

  const salvarDadosUsuario = async () => {
    if (estabelecimentoMinerador === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'O campo Estabelecimento Minerador é obrigatório.',
      });
      return;
    } else if (titularOuArrendatario === 0) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'O campo Tipo de Vínculo é obrigatório.',
      });
      return;
    } else if (!validarDocumento(estabelecimentoMinerador)) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'O campo Estabelecimento Minerador deve ser um CPF ou CNPJ válido.',
      });
      return;
    }
    const dadosOutrosRegimes: IDadosOutrosRegimesEnviar = {
      idDeclaracaoItem: parseInt(idOperacao),
      estabelecimentoMinerador: estabelecimentoMinerador,
      tipoVinculo: titularOuArrendatario,
      houveFatoGerador: hasFatoGerador,
      houveProducaoBemMineral: hasProducao,
      observacoes: observacoes,
    };

    await putSalvarDadosOutrosRegimes(dadosOutrosRegimes)
      .then(() => {
        hasFatoGerador ? setStepFormulario(2) : setIsEdicaoOutrosRegimes(false);
      })
      .catch((err) =>
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao salvar dados de Outros Regimes. Por favor, tente novamente.',
          complementoSentryId: err,
        })
      );
  };

  const buttons = () => {
    if (stepFormulario === 1) {
      return buttonsHtml(
        () => setIsEdicaoOutrosRegimes(false),
        () => salvarDadosUsuario(),
        <>
          <i className={`fas fa-times`} /> Cancelar
        </>,
        <>
          <i className={hasFatoGerador ? `fas fa-arrow-right` : `fas fa-save`} />{' '}
          {hasFatoGerador ? 'Salvar e Avançar' : 'Salvar e Finalizar'}
        </>
      );
    } else if (stepFormulario === 2 && baseDeCalculo === 'lista') {
      return buttonsHtml(
        () => setStepFormulario(1),
        () => setIsEdicaoOutrosRegimes(false),
        <>
          <i className={`fas fa-arrow-left`} /> Voltar
        </>,
        <>
          <i className={`fas fa-save`} /> Salvar e Finalizar
        </>
      );
    } else {
      return <></>;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.containerPrincipal}>
        <div className={styles.stepFormularioContainer}>
          <CustomStep
            stepAtual={stepFormulario}
            setStepFormulario={setStepFormulario}
            steps={[{ label: 'Dados da Operação' }, { label: 'Componentes da Base de Cálculo da CFEM' }]}
            withSuccess={false}
          />
        </div>
        <div className={styles.formularioContainer}>
          <div className={styles.titleContainer}>
            <div className={styles.title}>
              <h4 className={styles.titleText}>Processo Minerário</h4>
              <span className={styles.subtitleText}>Outros Regimes</span>
            </div>
            <span className={styles.mesAno}>
              {mesParaAbreviacao(parseInt(sessionStorage.getItem('mesDeclaracao') ?? '-1'))}{' '}
              {sessionStorage.getItem('anoDeclaracao')}
            </span>
          </div>
          <div className={styles.formulario}>
            {stepFormulario === 1 ? (
              <DadosOperacaoOutrosRegimes
                hasProducao={hasProducao}
                setHasProducao={setHasProducao}
                hasFatoGerador={hasFatoGerador}
                setHasFatoGerador={setHasFatoGerador}
                estabelecimentoMinerador={estabelecimentoMinerador}
                setEstabelecimentoMinerador={setEstabelecimentoMinerador}
                titularOuArrendatario={titularOuArrendatario}
                setTitularOuArrendatario={setTitularOuArrendatario}
                observacoes={observacoes}
                setObservacoes={setObservacoes}
              />
            ) : (
              <ComponentesBaseCalculoOutrosRegimes
                setStepFormulario={setStepFormulario}
                baseDeCalculo={baseDeCalculo}
                setBaseDeCalculo={setBaseDeCalculo}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles.containerButton}>{buttons()}</div>
    </div>
  );
};

export default TitularArrendatarioOutrosRegimes;
